import { FC, useCallback, useEffect, useState } from 'react';

import styled from 'styled-components';
import moment from 'moment';

import { useBreakpoint } from '@contexts/breakpoints';
import useSearchBar from '@contexts/searchbar';
import {
  DateType,
  formatMomentDate,
  formatShortLocalDate,
} from '@utils/dateUtil';
import { getDateRangePickerPlaceholder } from '@components/views/flight/listing/SearchBar/DateSelect/utils';
import { FlightSearchBarActionType } from '@contexts/searchbar/types/flight';
import ResponsiveSingleDateSelect from '@components/views/flight/listing/SearchBar/DateSelect/ResponsiveSingleDateSelect';
import {
  SearchBarSingleDatePicker,
  SearchBarRangeDatePicker,
} from '@components/views/flight/listing/SearchBar/DateSelect/DateSelect';
import ResponsiveDateRangeSelectDrawer from '@components/views/flight/listing/SearchBar/DateSelect/ResponsiveDateRangeSelect';
import { getViewportConstrainedPopoverLocation } from '@components/ui/search/SearchBarInput/getViewportConstrainedPopoverLocation';

const SearchBarDateInputWrapper = styled.div`
  width: 100%;
`;

interface Dates {
  startDate?: DateType | null;
  endDate?: DateType | null;
}

const FlightDatePicker: FC<{
  initialDates?: Dates;
  isTravelPlanning?: boolean;
}> = ({ initialDates, isTravelPlanning }) => {
  const {
    state: { flight: state },
    dispatch,
  } = useSearchBar();
  const { xl } = useBreakpoint();

  const [localStartDate, setLocalStartDate] = useState<DateType | null>(
    initialDates?.startDate || null
  );
  const [localEndDate, setLocalEndDate] = useState<DateType | null>(
    initialDates?.endDate || null
  );
  const [showDatepicker, setShowDatepicker] = useState(false);
  const [isCalendarReady, setIsCalendarReady] = useState(false);

  const handleCalendarReady = useCallback(() => {
    setIsCalendarReady(true);
  }, []);

  const handleDesktopPopoverVisibleChange = useCallback((visible: boolean) => {
    setIsCalendarReady(false);
    setShowDatepicker(visible);
  }, []);

  useEffect(() => {
    setIsCalendarReady(false);
    setShowDatepicker(false);

    if (!state.isOneWay) {
      const diff = moment(localEndDate?.toDate()).diff(
        localStartDate?.toDate(),
        'days'
      );

      if (diff < 0) {
        dispatch({
          type: FlightSearchBarActionType.SET_END_DATE,
          payload: null,
        });
        setLocalEndDate(null);
      }
    }
  }, [state.isOneWay]);

  useEffect(
    () => setLocalStartDate(state.startDate ? moment(state.startDate) : null),
    [state.startDate]
  );

  useEffect(
    () => setLocalEndDate(state.endDate ? moment(state.endDate) : null),
    [state.endDate]
  );

  const onDatesChange = ({
    startDate,
    endDate,
  }: {
    startDate: DateType | null;
    endDate: DateType | null;
  }) => {
    dispatch({
      type: FlightSearchBarActionType.SET_START_DATE,
      payload: startDate,
    });
    dispatch({
      type: FlightSearchBarActionType.SET_END_DATE,
      payload: endDate,
    });

    setLocalStartDate(startDate);
    setLocalEndDate(endDate);
  };

  const onDateChange = (date: moment.Moment | null) => {
    setLocalStartDate(date);
    dispatch({
      type: FlightSearchBarActionType.SET_START_DATE,
      payload: date,
    });
  };

  return (
    <SearchBarDateInputWrapper>
      {state.isOneWay ? (
        xl || xl === undefined ? (
          <SearchBarSingleDatePicker
            date={localStartDate}
            onDateChange={onDateChange}
            onReady={handleCalendarReady}
            inputComponent='shopAndFlyHomePageFlight'
            popoverContentReady={isCalendarReady}
            popoverProps={{
              isOpen: showDatepicker,
              onVisibleChange: handleDesktopPopoverVisibleChange,
              contentLocation: isTravelPlanning
                ? getViewportConstrainedPopoverLocation
                : undefined,
            }}
          />
        ) : (
          <ResponsiveSingleDateSelect
            date={localStartDate}
            onDateChange={onDateChange}
            title='Gidiş Tarihiniz?'
            subTitle={`${formatShortLocalDate(
              formatMomentDate(localStartDate)
            )}`}
            placeholder='Gidiş Tarihi'
            drawerTitle='Uçuş Tarihleri'
            arrowIcon={true}
            inputComponent='shopAndFlyHomePageFlight'
            useTravelPlanningSurface={isTravelPlanning}
          />
        )
      ) : null}

      {!state.isOneWay ? (
        xl || xl === undefined ? (
          <SearchBarRangeDatePicker
            minimumNights={0}
            startDate={localStartDate}
            endDate={localEndDate}
            onDatesChange={onDatesChange}
            onReady={handleCalendarReady}
            inputComponent='shopAndFlyHomePageFlight'
            popoverContentReady={isCalendarReady}
            popoverProps={{
              isOpen: showDatepicker,
              onVisibleChange: handleDesktopPopoverVisibleChange,
              contentLocation: isTravelPlanning
                ? getViewportConstrainedPopoverLocation
                : undefined,
            }}
          />
        ) : (
          <ResponsiveDateRangeSelectDrawer
            startDate={localStartDate}
            endDate={localEndDate}
            onDatesChange={onDatesChange}
            title='Gidiş-Dönüş Tarihiniz?'
            subTitle={getDateRangePickerPlaceholder(
              localStartDate,
              localEndDate
            )}
            placeholder='Gidiş - Dönüş Tarihleri'
            drawerTitle='Uçuş Tarihleri'
            arrowIcon={true}
            inputComponent='shopAndFlyHomePageFlight'
          />
        )
      ) : null}
    </SearchBarDateInputWrapper>
  );
};
export default FlightDatePicker;
